<template>
  <div class="club-league">
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-container">
      <v-progress-circular indeterminate :color="clubColor" :size="50" />
    </div>

    <!-- Main Content -->
    <div v-else-if="!isLoading && hasPartner && visibleTiers.length > 0">
      <div class="league-header mb-4">
        <h2 class="league-title">{{ $t('league') }} {{ tierLabel }}</h2>
        <p class="league-subtitle">{{ $t('clubLeague.subtitle') }}</p>
      </div>

      <!-- Tier Selector -->
      <div class="tier-selector mb-6">
        <div class="tabs">
          <template v-for="(tier, index) in visibleTiers">
            <input
              :key="`input-${index}`"
              type="radio"
              :id="`tier-${index}`"
              :value="index"
              v-model="selectedVisibleTierIndex"
              @change="onTierChange(index)" />
            <label
              :key="`label-${index}`"
              class="tab"
              :for="`tier-${index}`">
              <img :src="tier.icon" :alt="tier.label" class="tier-icon" />
              <span class="tab-label">{{ tier.label }}</span>
            </label>
          </template>
          <span class="glider" :style="gliderStyle"></span>
        </div>
      </div>

      <!-- Podium Section -->
      <div v-if="top3Communities.length > 0" class="podium-section mb-6">
        <LeaguePodiumCard
          v-for="community in podiumOrder"
          :key="community.id"
          :community="community"
          :place="community.rank"
          :highlight="community.id === currentClubId"
        />
      </div>

      <!-- Full Table -->
      <div v-if="communities.length > 0" class="table-section">
        <LeagueTable
          :communities="communities"
          :primary-color="clubColor"
          :highlight-club-id="currentClubId"
        />
      </div>

      <!-- Empty State -->
      <div v-if="communities.length === 0" class="empty-state">
        <v-icon size="80" :color="clubColor" class="mb-4">fa-regular fa-swords</v-icon>
        <h3>{{ $t('clubLeague.noData') }}</h3>
        <p>{{ $t('clubLeague.noDataDescription') }}</p>
      </div>
    </div>

    <!-- No Partner Empty State -->
    <div v-else-if="!hasPartner && !isLoading" class="empty-state">
      <v-icon size="80" color="grey" class="mb-4">fa-regular fa-swords</v-icon>
      <h3>{{ $t('clubLeague.noPartner') }}</h3>
      <p>{{ $t('clubLeague.noPartnerDescription') }}</p>
    </div>

    <!-- Not in Any League Empty State -->
    <div v-else-if="hasPartner && !isLoading && visibleTiers.length === 0" class="empty-state">
      <v-icon size="80" :color="clubColor" class="mb-4">fa-regular fa-swords</v-icon>
      <h3>{{ $t('clubLeague.notInLeague') }}</h3>
      <p>{{ $t('clubLeague.notInLeagueDescription') }}</p>
    </div>
  </div>
</template>

<script>
import LeaguePodiumCard from '@/apps/broker/components/Leagues/LeaguePodiumCard.vue';
import LeagueTable from '@/apps/broker/components/Leagues/LeagueTable.vue';
import leaguesRepository from '@/apps/broker/repositories/leagues.repository';

export default {
  name: 'ClubLeague',
  components: {
    LeaguePodiumCard,
    LeagueTable
  },
  data() {
    return {
      isLoading: true,
      selectedVisibleTierIndex: 0,
      communities: [],
      tierPresence: {}, // Track which tiers have the club present
      tiers: [
        {
          value: 'platinum',
          label: 'Platinum',
          icon: require('@/apps/broker/designs/leagues/platinum-icon.png'),
          apiValue: 'DIAMOND'
        },
        {
          value: 'gold',
          label: 'Gold',
          icon: require('@/apps/broker/designs/leagues/gold-icon.png'),
          apiValue: 'GOLD'
        },
        {
          value: 'silver',
          label: 'Silver',
          icon: require('@/apps/broker/designs/leagues/silver-icon.png'),
          apiValue: 'SILVER'
        },
        {
          value: 'bronze',
          label: 'Bronze',
          icon: require('@/apps/broker/designs/leagues/bronze-icon.png'),
          apiValue: 'BRONZE'
        }
      ]
    };
  },
  computed: {
    clubColor() {
      const club = this.$store.state.clubMember?.club;

      // Priority: Club's own color > Partner's color > Default green
      let color = '#009F63'; // Default fallback

      if (club?.primaryColor) {
        color = `#${club.primaryColor}`;
      } else if (club?.partner?.mainColor) {
        // Use partner's color if club doesn't have its own
        color = `#${club.partner.mainColor}`;
      }

      console.log('ClubLeague clubColor computed:', color);
      console.log('ClubLeague club:', club);
      return color;
    },
    currentClubId() {
      return this.$store.state.clubMember?.club?.id;
    },
    partnerId() {
      return this.$store.state.clubMember?.club?.partner?.id || null;
    },
    hasPartner() {
      return this.partnerId !== null;
    },
    visibleTiers() {
      // Only show tiers where the club is present in the ranking
      return this.tiers.filter(tier => this.tierPresence[tier.apiValue] === true);
    },
    selectedTier() {
      return this.visibleTiers[this.selectedVisibleTierIndex];
    },
    tierLabel() {
      return this.selectedTier?.label || '';
    },
    gliderStyle() {
      const tabCount = this.visibleTiers.length || 1;
      const widthPercentage = 100 / tabCount;

      return {
        transform: `translateX(${this.selectedVisibleTierIndex * 100}%)`,
        backgroundColor: this.clubColor,
        width: `${widthPercentage}%`
      }
    },
    tabTextColor() {
      // Calculate YIQ contrast to determine if text should be black or white
      const color = this.clubColor.replace('#', '');
      const r = parseInt(color.substr(0, 2), 16);
      const g = parseInt(color.substr(2, 2), 16);
      const b = parseInt(color.substr(4, 2), 16);
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return yiq >= 128 ? '#000000' : '#FFFFFF';
    },
    top3Communities() {
      return this.communities.slice(0, 3).map((c, index) => ({
        ...c,
        rank: index + 1
      }));
    },
    podiumOrder() {
      if (this.top3Communities.length < 2) return this.top3Communities;
      const ordered = [];
      if (this.top3Communities[1]) ordered.push(this.top3Communities[1]); // 2nd
      if (this.top3Communities[0]) ordered.push(this.top3Communities[0]); // 1st
      if (this.top3Communities[2]) ordered.push(this.top3Communities[2]); // 3rd
      return ordered;
    }
  },
  watch: {},
  async mounted() {
    await this.init();
  },
  methods: {
    async init() {
      console.log('ClubLeague init - partnerId:', this.partnerId, 'hasPartner:', this.hasPartner);
      this.isLoading = true;
      try {
        if (this.hasPartner) {
          // Check all tiers to see which ones have the club
          await this.checkAllTiersPresence();
          // Fetch data for the first visible tier
          if (this.visibleTiers.length > 0) {
            await this.fetchLeagueData();
          }
        }
      } catch (error) {
        console.error('Error initializing league:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async checkAllTiersPresence() {
      // Check each tier to see if the club is present
      const presenceChecks = this.tiers.map(async (tier) => {
        try {
          const response = await leaguesRepository.getLeagueOverview(
            this.partnerId,
            'IBS_LEAGUE',
            tier.apiValue
          );
          // Check if current club is in the ranking
          const isPresent = response.clubRanking.some(club => club.clubId === this.currentClubId);
          this.$set(this.tierPresence, tier.apiValue, isPresent);
        } catch (error) {
          console.error(`Error checking tier ${tier.apiValue}:`, error);
          this.$set(this.tierPresence, tier.apiValue, false);
        }
      });

      await Promise.all(presenceChecks);
      console.log('Tier presence check:', this.tierPresence);
    },
    onTierChange(index) {
      this.selectedVisibleTierIndex = index;
      this.fetchLeagueData();
    },
    async fetchLeagueData() {
      if (!this.partnerId || !this.selectedTier) return;

      this.isLoading = true;
      try {
        console.log('Fetching league data for partnerId:', this.partnerId, 'tier:', this.selectedTier.apiValue);
        const response = await leaguesRepository.getLeagueOverview(
          this.partnerId,
          'IBS_LEAGUE',
          this.selectedTier.apiValue
        );
        console.log('League data response:', response);

        // Map API response to component data structure
        this.communities = response.clubRanking.map(club => ({
          id: club.clubId,
          name: club.clubName,
          avatar: club.clubImageUrl,
          volume: club.totalVolume,
          points: null,
          rank: club.rank,
          memberCount: club.memberCount
        }));
      } catch (error) {
        console.error('Error fetching league data:', error);
        this.communities = [];
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.club-league {
  padding: 20px;
  min-height: 400px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.league-header {
  .league-title {
    font-family: 'Avenir', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: var(--v-mainBlack-base, #1A1A1A);
    margin: 0 0 8px 0;
  }

  .league-subtitle {
    font-family: 'Avenir', sans-serif;
    font-size: 16px;
    color: #666666;
    margin: 0;
  }
}

.tier-selector {
  width: 100%;
  margin-bottom: 24px;

  .tabs {
    display: flex;
    position: relative;
    background-color: var(--v-lightGray-base);
    border-radius: 10px;
    width: 100%;

    * {
      z-index: 2;
    }

    input[type="radio"] {
      display: none;

      &:checked {
        & + label {
          color: v-bind(tabTextColor) !important;

          .tier-icon {
            filter: brightness(0) saturate(100%);
            opacity: 0.9;
          }
        }
      }
    }

    .tab {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      height: 38px;
      font-size: 14px;
      font-weight: 600;
      color: var(--v-mainBlack-base);
      border-radius: 10px;
      cursor: pointer;
      transition: color 0.15s ease-in;
      font-family: "Avenir", sans-serif;
      padding: 0 16px;

      .tier-icon {
        width: 20px;
        height: 20px;
        margin-right: 8px;
      }

      .tab-label {
        white-space: nowrap;
      }
    }

    .glider {
      position: absolute;
      display: flex;
      height: 38px;
      border-radius: 10px;
      z-index: 1;
      transition: 0.25s ease-out;
    }
  }
}

.theme--dark .tier-selector {
  .tabs {
    background-color: #303740;

    .tab {
      color: var(--v-fullWhite-base);

      &:hover {
        opacity: 0.8;
      }
    }
  }
}

.podium-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  align-items: end;
}

.table-section {
  margin-bottom: 32px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;

  h3 {
    font-family: 'Avenir', sans-serif;
    font-size: 24px;
    font-weight: 600;
    color: var(--v-mainBlack-base, #1A1A1A);
    margin: 0 0 8px 0;
  }

  p {
    font-family: 'Avenir', sans-serif;
    font-size: 16px;
    color: #666666;
    margin: 0;
    max-width: 500px;
    line-height: 1.6;
  }
}

// Responsive
@media (max-width: 768px) {
  .podium-section {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .tier-selector {
    overflow-x: auto;
    justify-content: flex-start;
  }
}
</style>
